import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import * as patientActions from '@patient-ui/patient-web/store';
import { PatientState, patientQuery } from '@patient-ui/patient-web/store';
import { IAppointment, PortalUser } from '@patient-ui/shared/models';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import { AppointmentDetailsService } from './appointment-details.service';
import {
  getDataUri,
  getRawIcsString,
  triggerDataUriDownload,
} from '../utils/calendar.utils';

@Injectable({
  providedIn: 'root',
})
export class AppointmentActionsService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private appointmentDetailsService: AppointmentDetailsService,
    private store: Store<PatientState>,
    private environmentService: EnvironmentService
  ) {}

  /**
   * Check if browser supports native share API
   */
  get canShare(): boolean {
    return (
      typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    );
  }

  /**
   * Navigate to review/modify page
   * Fetches full appointment details from API before navigating (unless already provided)
   * Uses /appointments/me/{confirmationCode}/find endpoint for registered users
   *
   * @param appointment - Basic appointment info (from list)
   * @param existingDetails - Optional full details (from details page) to skip redundant API call
   */
  async navigateToReview(
    appointment: IAppointment,
    existingDetails?: unknown
  ): Promise<void> {
    sessionStorage.setItem('appointmentReviewReferrer', this.router.url);

    // Store the currently selected user to restore after navigation back
    this.store
      .select(patientQuery.selectPatientAppointmentsDropdownUser)
      .pipe(take(1))
      .subscribe((selectedUser) => {
        if (selectedUser) {
          sessionStorage.setItem(
            'appointmentReviewSelectedUser',
            JSON.stringify(selectedUser)
          );
        }
      });

    try {
      let appointmentDetails = existingDetails;

      // Only fetch from API if we don't already have the details
      if (!appointmentDetails) {
        // Call /appointments/me/{confirmationCode}/find API for registered users
        appointmentDetails = await this.appointmentDetailsService
          .getAppointmentDetails(String(appointment.confirmationNum))
          .toPromise();
      }

      // Store the full API response for use in review page and modal
      sessionStorage.setItem(
        'appointmentDetails',
        JSON.stringify(appointmentDetails)
      );

      // Store the patient ID associated with this appointment (from dropdown selection)
      // This will be used to get the correct encrypted LPID for cancel/modify operations
      this.store
        .select(patientQuery.selectPatientAppointmentsDropdownUser)
        .pipe(take(1))
        .subscribe((selectedUser) => {
          if (selectedUser && selectedUser.id) {
            sessionStorage.setItem(
              `appointmentPatientId_${appointment.confirmationNum}`,
              String(selectedUser.id)
            );
          }
        });

      // Also keep the old format for backward compatibility
      sessionStorage.setItem(
        'selectedAppointment',
        JSON.stringify(appointment)
      );

      // Navigate to the review page
      this.router.navigate(['/portal/appointments/review'], {
        state: { appointment },
      });
    } catch (error) {
      // Fallback to old behavior if API fails
      sessionStorage.setItem(
        'selectedAppointment',
        JSON.stringify(appointment)
      );
      this.router.navigate(['/portal/appointments/review'], {
        state: { appointment },
      });
    }
  }

  /**
   * Share appointment details using Web Share API
   */
  async share(appointment: IAppointment): Promise<void> {
    if (!this.canShare) {
      return;
    }

    const shareText = this.formatAppointmentShareText(appointment);

    try {
      await navigator.share({
        text: shareText,
      });
    } catch (error) {}
  }

  /**
   * Generate and download ICS calendar file
   */
  async addToCalendar(appointment: IAppointment): Promise<void> {
    try {
      const appointmentDateTime = this.parseAppointmentDateTime(
        appointment.apptDate,
        appointment.apptTime
      );

      const location = this.formatLocation(appointment.location);
      const shareText = this.formatAppointmentShareText(appointment);

      const rawIcsString = await getRawIcsString({
        dateTime: appointmentDateTime,
        text: shareText,
        location: location,
        url: appointment.confirmationURL || '',
      });

      const dataUri = getDataUri(rawIcsString);
      triggerDataUriDownload(this.document, dataUri, 'LabcorpAppointment.ics');
    } catch (error) {}
  }

  /**
   * Navigate to Billing Dashboard with patient context
   * Sets the selected patient in store and navigates to billing page
   * Preserves "All Patients" selection if currently selected on appointments page
   */
  async navigateToBilling(appointment: IAppointment): Promise<void> {
    // Get the currently selected user from store
    const currentSelectedUser = await this.store
      .select(patientQuery.selectPatientAppointmentsDropdownUser)
      .pipe(take(1))
      .toPromise();

    // Check if "All Patients" is currently selected
    if (
      currentSelectedUser &&
      (currentSelectedUser.displayName === 'All Patients' ||
        currentSelectedUser.id === 0)
    ) {
      // Dispatch "All Patients" to ensure billing page shows all patients
      this.store.dispatch(
        patientActions.selectOpenInvoiceUser({
          payload: {
            id: 0,
            displayName: 'All Patients',
          },
        })
      );
    } else {
      // Create PortalUser object from appointment data
      // Use patientId (not locatorId) for the user ID
      const selectedUser: PortalUser = {
        id: appointment.patientId
          ? parseInt(appointment.patientId, 10)
          : appointment.locatorId,
        displayName: 'Patient',
      };

      // Store patient ID in localStorage for billing page to read
      localStorage.setItem('selectedUserId', selectedUser.id.toString());

      // Dispatch action to set selected patient for invoices
      this.store.dispatch(
        patientActions.selectOpenInvoiceUser({ payload: selectedUser })
      );
    }

    // Set flag in sessionStorage to indicate navigation from appointments
    // This helps billing page distinguish between:
    // 1. Navigation from appointments (preserve selection)
    // 2. Direct URL navigation (default to PAH)
    sessionStorage.setItem('billingNavigationSource', 'appointments');

    // Navigate to billing dashboard
    this.router.navigate(['/portal/invoices/billing']);
  }

  /**
   * Navigate to Lab Orders page with patient context
   * Uses sessionStorage flag to preserve user selection from appointments
   */
  async navigateToOrders(): Promise<void> {
    // Set flag in sessionStorage to indicate navigation from appointments
    // The orders page will read this flag to determine whether to:
    // 1. Preserve user selection from appointments (if flag is present)
    // 2. Default to PAH (if direct navigation without flag)
    sessionStorage.setItem('ordersNavigationSource', 'appointments');

    // Store the selected user context for orders page to read
    const currentSelectedUser = await this.store
      .select(patientQuery.selectPatientAppointmentsDropdownUser)
      .pipe(take(1))
      .toPromise();

    if (currentSelectedUser) {
      sessionStorage.setItem(
        'ordersSelectedUser',
        JSON.stringify(currentSelectedUser)
      );
    }

    // Navigate to lab orders page
    this.router.navigate(['/portal/orders']);
  }

  /**
   * Navigate to Appointment Details page
   * Navigates to the details page for the given appointment
   */
  navigateToDetails(appointment: IAppointment): void {
    this.router.navigate([
      '/portal/appointments/detail',
      appointment.confirmationNum,
    ]);
  }

  /**
   * Navigate to Mobile Check-in from a CONFIRMATION CODE alone (PR 3170 review).
   *
   * The check-in page has no route param: it reads the whole appointment out of
   * `sessionStorage.checkInAppointmentDetails` in `ngOnInit` and bounces to
   * `/portal/appointments` when that key is absent. So the seed is not optional
   * polish — a bare `router.navigate(['.../check-in'])` is a dead end, which is
   * how Orbi's card was reaching it.
   *
   * The find API response IS the shape the page expects
   * (`AppointmentDetailsResponse`, keyed `confirmationCode` / `appointmentDateTime`
   * / `lpid`), so the seed is the raw response, stored unmapped — exactly what
   * `appointment-detail-view` writes from its own already-loaded details. Nothing
   * is hand-stubbed: a partial object here would load the page and then fail at
   * submit, when `confirmationCode` and `lpid` go to the check-in API.
   *
   * Takes the code rather than an `IAppointment` because callers that hold only a
   * confirmation number (the Orbi cards) have nothing else the page uses.
   * Rejects the promise if the lookup fails, so the caller can keep the user where
   * they are instead of landing them on a page that immediately redirects.
   */
  async navigateToCheckIn(confirmationCode: string): Promise<void> {
    const appointmentDetails = await this.appointmentDetailsService
      .getAppointmentDetails(confirmationCode)
      .toPromise();

    sessionStorage.setItem(
      'checkInAppointmentDetails',
      JSON.stringify(appointmentDetails)
    );

    this.router.navigate(['/portal/appointments/check-in']);
  }

  /**
   * Navigate to the Insurance Entry Flow from a CONFIRMATION CODE alone
   * (PR 3170 review) — the counterpart to `navigateToCheckIn` above, for callers
   * that hold no `IAppointment`.
   *
   * `insurance-provider` reads `insuranceFlowAppointment` and redirects to
   * `/portal/appointments` without it, so this entry point needs the same seed
   * `navigateToInsurance` writes. The flow consumes exactly three fields of the
   * stored appointment — `confirmationNum`, `locatorId` and `lpid` (the provider
   * page's insurance-plan lookup and scan flag, and the submission payload) — and
   * the find response carries all three, so the mapping below is complete for
   * this flow rather than a partial stand-in. The remaining `IAppointment`
   * members are filled from the response where it has them; none of them are read
   * downstream of here.
   */
  async navigateToInsuranceByConfirmation(
    confirmationCode: string
  ): Promise<void> {
    const details = await this.appointmentDetailsService
      .getAppointmentDetails(confirmationCode)
      .toPromise();

    if (!details) {
      throw new Error(
        `No appointment details for confirmation ${confirmationCode}`
      );
    }

    const appointment: IAppointment = {
      confirmationNum: details.confirmationCode,
      locatorId: details.location.locatorId,
      lpid: details.lpid,
      apptDate: details.appointmentDateTime,
      apptTime: details.appointmentDateTime,
      serviceName: details.serviceType,
      patientId: details.patientId ?? '',
      past: false,
      paidBy: details.paidBy ?? '',
      location: {
        locatorId: details.location.locatorId,
        address: {
          street: details.location.address.street,
          line2: details.location.address.line2 ?? '',
          city: details.location.address.city,
          state: details.location.address.state,
          zipCode: details.location.address.zipCode,
        },
      },
    };

    // Seeds directly rather than delegating to `navigateToInsurance`: that method
    // re-fetches the same find response to pick up `lpid`, which this mapping
    // already carries. Both paths write the same two keys (below).
    this.seedInsuranceFlow(appointment);
  }

  /**
   * Write the session state `insurance-provider` reads, then go there.
   *
   * `insuranceFlowAppointment` is the appointment the flow runs against, and
   * `insuranceFlowReferrer` is where submit/cancel returns to. The stale
   * `insuranceReviewReferrer` is cleared so the user lands back where they started
   * (e.g. the Dashboard) rather than on the insurance review page.
   */
  private seedInsuranceFlow(appointment: IAppointment): void {
    sessionStorage.removeItem('insuranceReviewReferrer');
    sessionStorage.setItem('insuranceFlowReferrer', this.router.url);
    sessionStorage.setItem(
      'insuranceFlowAppointment',
      JSON.stringify(appointment)
    );
    this.router.navigate(['/portal/appointments/insurance/provider']);
  }

  /**
   * Navigate to Insurance Entry Flow
   * Fetches lpid from find API to ensure correct lpid for each appointment
   * IMPORTANT: Must use find API because appointment could belong to linked account
   * Store only has lpid for selected patient, not all patients in "All Patients" view
   */
  async navigateToInsurance(appointment: IAppointment): Promise<void> {
    try {
      // Clear any stale insuranceReviewReferrer from previous flows
      // This ensures users return to their original location (e.g., Dashboard)
      // rather than being redirected to the insurance review page
      sessionStorage.removeItem('insuranceReviewReferrer');

      // Store current URL as referrer for navigation back after submission/cancel
      sessionStorage.setItem('insuranceFlowReferrer', this.router.url);

      // Fetch appointment details from find API to get correct lpid
      // This ensures we get the right lpid even when "All Patients" is selected
      const appointmentDetails = await this.appointmentDetailsService
        .getAppointmentDetails(String(appointment.confirmationNum))
        .toPromise();

      // Enrich appointment with lpid from API response
      const enrichedAppointment: IAppointment = {
        ...appointment,
        lpid: appointmentDetails?.lpid,
      };

      // Store enriched appointment data in session storage for insurance flow
      sessionStorage.setItem(
        'insuranceFlowAppointment',
        JSON.stringify(enrichedAppointment)
      );

      // Navigate to insurance provider page
      this.router.navigate(['/portal/appointments/insurance/provider']);
    } catch (error) {
      // Store current URL as referrer
      sessionStorage.setItem('insuranceFlowReferrer', this.router.url);

      // Fallback: store appointment without lpid (will show error at submission)
      sessionStorage.setItem(
        'insuranceFlowAppointment',
        JSON.stringify(appointment)
      );
      this.router.navigate(['/portal/appointments/insurance/provider']);
    }
  }

  /**
   * Open Rate Visit page in new tab
   * Navigates to external rate visit URL with appointment location details
   * @param appointment - Appointment to rate
   */
  rateVisit(appointment: IAppointment): void {
    const baseUrl = this.environmentService.rateVisitBaseUrl;
    if (!baseUrl || !appointment.location?.address) {
      return;
    }

    const params = new URLSearchParams({
      id: appointment.location.locatorId.toString(),
      address: appointment.location.address.street,
      city: appointment.location.address.city,
      state: appointment.location.address.state,
      zip: appointment.location.address.zipCode,
    });

    const url = `${baseUrl}/rate?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Format appointment details for sharing
   */
  private formatAppointmentShareText(appointment: IAppointment): string {
    const location = this.formatLocation(appointment.location);
    // Remove query parameters from the confirmation URL
    const shareUrl = appointment.confirmationURL
      ? appointment.confirmationURL.split('?')[0]
      : '';
    return `Labcorp Appointment Details
Confirmation Number: ${appointment.confirmationNum}

Lab Location: ${location}
Appointment Date and Time: ${appointment.apptDate} ${appointment.apptTime}
Service Type: ${appointment.serviceName}

Access your appointment details here: ${shareUrl}`;
  }

  /**
   * Format location address as string
   */
  private formatLocation(location: IAppointment['location']): string {
    const parts = [location.address.street];
    if (location.address.line2) {
      parts.push(location.address.line2);
    }
    parts.push(
      `${location.address.city}, ${location.address.state} ${location.address.zipCode}`
    );
    return parts.join(', ');
  }

  /**
   * Parse appointment date and time strings into Date object
   */
  private parseAppointmentDateTime(
    dateString: string,
    timeString: string
  ): Date {
    // Parse the date string using Date constructor (works with ISO format and most standard formats)
    const baseDate = new Date(dateString);

    if (isNaN(baseDate.getTime())) {
      return new Date(); // Fallback to current date
    }

    // Parse time string (format like "3:30 PM" or "15:30" or "3:30pm")
    const timeMatch = timeString.trim().match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (!timeMatch) {
      return baseDate; // Return date without time adjustment
    }

    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const meridiem = timeMatch[3]?.toUpperCase();

    // Convert to 24-hour format if AM/PM is present
    if (meridiem) {
      if (meridiem === 'PM' && hours !== 12) {
        hours += 12;
      } else if (meridiem === 'AM' && hours === 12) {
        hours = 0;
      }
    }

    // Set the time on the date object
    baseDate.setHours(hours, minutes, 0, 0);

    return baseDate;
  }
}
